import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { update as updated } from '@app/utils/update';
import { ActionHandler, Filter, Product, ProductFilters, Sorting } from '@typings';
import { without } from 'ramda';
import { update } from 'space-lift';
import { createReducer } from 'typesafe-actions';

import {
  addAllProductsToOrderFailure,
  addAllProductsToOrderRequest,
  addAllProductsToOrderSuccess,
  addProductsToOrder,
  addProductsToOrderFailure,
  addProductsToOrderSuccess,
  copyToNewSelectionSuccess,
  createSelection,
  removeProductsFromOrder,
  removeProductsFromOrderFailure,
  removeProductsFromOrderSuccess,
  resetOrderData,
} from '../order';
import { fetchPageBySlugSuccess } from '../pages';

import * as actions from './actions';

export interface ProductsReducer {
  attributes?: Filter.Field<string>[];
  pageCount: number;
  totalItems: number;
  products: Record<string, Product.Full>;
  variantId: null;
  productVariantImages: Record<string, unknown>;
  filters: ProductFilters;
  sorting: Nullable<Sorting.FieldValue>;
  loadingProducts: Product.Id[];
  expandedFilters: {
    firstFilter: string | undefined;
    states: Record<string, boolean>;
  };
}

const initialState: ProductsReducer = {
  expandedFilters: {
    firstFilter: undefined,
    states: {},
  },
  filters: {},
  loadingProducts: [],
  pageCount: 1,
  productVariantImages: {},
  products: {},
  sorting: null,
  totalItems: 0,
  variantId: null,
};

export { initialState as initialProductsState };

const handleSetProductSortingValue: ActionHandler<ProductsReducer, typeof actions.setProductSortingValue> = (state, action) => {
  return updated(state, {
    sorting: action.payload,
  });
};

const handleSetProductSortingDefaultValue: ActionHandler<ProductsReducer, typeof actions.setProductSortingDefaultValue> = (
  state,
  action,
) => {
  return updated(state, {
    sorting: action.payload,
  });
};

const handleUpdateFilters: ActionHandler<ProductsReducer, typeof actions.updateFilters> = (state, { payload }) => {
  const { filters, preserveCurrent } = payload;
  const updatedFilters = preserveCurrent ? { ...state.filters, ...filters } : { ...initialState.filters, ...filters };

  return update(state, {
    filters: updatedFilters as ProductFilters,
    pageCount: 1,
  });
};

const handleProductLoadingFailed: ActionHandler<
  ProductsReducer,
  typeof addProductsToOrderFailure | typeof removeProductsFromOrderFailure
> = (state, action) => {
  const { products } = action.payload;

  return updated(state, {
    loadingProducts: without(products, state.loadingProducts),
  });
};

const handleResetFilters = (state: ProductsReducer): ProductsReducer => {
  const { stockType } = state.filters;

  return updated(state, {
    filters: {
      ...initialState.filters,
      ...(isDefined(stockType) ? { stockType } : {}),
    },
    pageCount: 1,
  });
};

const handleAddProductsToOrder: ActionHandler<ProductsReducer, typeof addProductsToOrder> = (state, action) => {
  const { products } = action.payload;

  return updated(state, {
    loadingProducts: [...state.loadingProducts, ...products],
  });
};

const handleAddProductsToOrderSuccess: ActionHandler<ProductsReducer, typeof addProductsToOrderSuccess> = (state, action) => {
  const { variantsAdded, variantsNotAdded } = action.payload.data;

  const modifiedProducts =
    variantsAdded.length > 0 ? variantsAdded.map(added => added.product) : variantsNotAdded.map(notAdded => notAdded.product);

  return updated(state, {
    loadingProducts: without(modifiedProducts, state.loadingProducts),
  });
};

const handleAddAllProductsToOrderRequest: ActionHandler<ProductsReducer, typeof addAllProductsToOrderRequest> = (state, action) => {
  const { products } = action.payload;

  return updated(state, {
    loadingProducts: [...state.loadingProducts, ...products],
  });
};

const handleAddAllProductsToOrderSuccess: ActionHandler<ProductsReducer, typeof addAllProductsToOrderSuccess> = state => {
  return updated(state, { loadingProducts: [] });
};

const handleAddAllProductsToOrderFailure: ActionHandler<ProductsReducer, typeof addAllProductsToOrderFailure> = state => {
  return updated(state, { loadingProducts: [] });
};

const handleRemoveProductsFromOrder: ActionHandler<ProductsReducer, typeof removeProductsFromOrder> = (state, action) => {
  const { products } = action.payload;

  return updated(state, {
    loadingProducts: [...state.loadingProducts, ...products],
  });
};

const handleRemoveProductsFromOrderSuccess: ActionHandler<ProductsReducer, typeof removeProductsFromOrderSuccess> = (state, action) => {
  const { variantsRemoved } = action.payload;
  const productIds = variantsRemoved.map(({ product }) => product);

  return updated(state, {
    loadingProducts: without(productIds, state.loadingProducts),
  });
};

const handleResetProducts = (state: ProductsReducer): ProductsReducer =>
  update(state, {
    products: {},
  });

const handlePageFetchSuccess: ActionHandler<ProductsReducer, typeof fetchPageBySlugSuccess> = (state, action) =>
  update(state, {
    products: { ...state.products, ...action.payload.products },
  });

const handleSetExpandedFilters: ActionHandler<ProductsReducer, typeof actions.setExpandedProductFilters> = (state, action) => {
  if (isEmpty(action.payload)) {
    return update(state, {
      expandedFilters: initialState.expandedFilters,
    });
  }

  return update(state, {
    expandedFilters: update(state.expandedFilters, {
      states: action.payload,
    }),
  });
};

export default createReducer<ProductsReducer, AppAction>(initialState)
  .handleAction(actions.resetFilters, handleResetFilters)
  .handleAction(actions.updateFilters, handleUpdateFilters)
  .handleAction(actions.setExpandedProductFilters, handleSetExpandedFilters)
  .handleAction(actions.setProductSortingValue, handleSetProductSortingValue)
  .handleAction(actions.setProductSortingDefaultValue, handleSetProductSortingDefaultValue)
  .handleAction(addProductsToOrder, handleAddProductsToOrder)
  .handleAction(addProductsToOrderSuccess, handleAddProductsToOrderSuccess)
  .handleAction(addProductsToOrderFailure, handleProductLoadingFailed)
  .handleAction(addAllProductsToOrderRequest, handleAddAllProductsToOrderRequest)
  .handleAction(addAllProductsToOrderSuccess, handleAddAllProductsToOrderSuccess)
  .handleAction(addAllProductsToOrderFailure, handleAddAllProductsToOrderFailure)
  .handleAction(removeProductsFromOrder, handleRemoveProductsFromOrder)
  .handleAction(removeProductsFromOrderSuccess, handleRemoveProductsFromOrderSuccess)
  .handleAction(removeProductsFromOrderFailure, handleProductLoadingFailed)
  .handleAction(createSelection, handleResetProducts)
  .handleAction(copyToNewSelectionSuccess, handleResetProducts)
  .handleAction(resetOrderData, handleResetProducts)
  .handleAction(fetchPageBySlugSuccess, handlePageFetchSuccess);
